import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import timerAPI, { ActiveTimerSession } from '../../services/timerAPI';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from 'notistack';

interface ActiveTimersProps {
  refreshInterval?: number; // in milliseconds, default 10000 (10 seconds)
}

export const ActiveTimers: React.FC<ActiveTimersProps> = ({ 
  refreshInterval = 10000 
}) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [activeSessions, setActiveSessions] = useState<ActiveTimerSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    session: ActiveTimerSession | null;
  }>({ open: false, session: null });
  const [stopping, setStopping] = useState(false);

  // Check permissions - only Admin and Bauleiter can see this component
  const canSeeActiveSessions = user?.role === 'admin' || user?.role === 'bauleiter';

  const fetchActiveSessions = async () => {
    if (!canSeeActiveSessions) return;
    
    try {
      const sessions = await timerAPI.getActiveSessions();
      setActiveSessions(sessions);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching active timer sessions:', err);
      setError('Fehler beim Laden der aktiven Zeiterfassungen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canSeeActiveSessions) {
      setLoading(false);
      return;
    }

    // Initial fetch
    fetchActiveSessions();

    // Set up polling
    const interval = setInterval(fetchActiveSessions, refreshInterval);

    return () => clearInterval(interval);
  }, [canSeeActiveSessions, refreshInterval]);

  // Format elapsed time
  const formatElapsedTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format start time
  const formatStartTime = (startTime: string): string => {
    return new Date(startTime).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format location for display
  const formatLocation = (location?: string): string => {
    if (!location) return 'Nicht verfügbar';
    
    // Check if it's actually numeric coordinates (e.g., "49.4948, 10.8060")
    if (location.includes(',')) {
      const parts = location.split(',');
      if (parts.length === 2) {
        const lat = parseFloat(parts[0].trim());
        const lng = parseFloat(parts[1].trim());
        
        // Only format as coordinates if both parts are valid numbers
        if (!isNaN(lat) && !isNaN(lng)) {
          return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        }
      }
    }
    
    // Return the location as-is (street address)
    return location;
  };

  // Handle timer stop click
  const handleStopClick = (session: ActiveTimerSession) => {
    setConfirmDialog({ open: true, session });
  };

  // Handle confirm stop
  const handleConfirmStop = async () => {
    if (!confirmDialog.session) return;
    
    setStopping(true);
    try {
      const result = await timerAPI.adminStopTimer(confirmDialog.session.user_id);
      
      // Show detailed success message with work hours and break time
      const detailedMessage = result.worked_hours && result.break_minutes 
        ? `${result.user_name}: ${result.worked_hours}h gearbeitet, ${result.break_minutes}min Pause. Zeiteintrag erstellt.`
        : result.message;
      
      enqueueSnackbar(detailedMessage, { variant: 'success' });
      
      // Refresh the active sessions list
      await fetchActiveSessions();
    } catch (error: any) {
      console.error('Error stopping timer:', error);
      const errorMessage = error?.response?.data?.detail || 'Fehler beim Beenden der Zeiterfassung';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setStopping(false);
      setConfirmDialog({ open: false, session: null });
    }
  };

  // Handle cancel stop
  const handleCancelStop = () => {
    setConfirmDialog({ open: false, session: null });
  };

  // Don't render if user doesn't have permission
  if (!canSeeActiveSessions) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <AccessTimeIcon color="primary" />
            <Typography variant="h6">
              Aktive Zeiterfassungen
            </Typography>
          </Box>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <AccessTimeIcon color="primary" />
            <Typography variant="h6">
              Aktive Zeiterfassungen
            </Typography>
          </Box>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <AccessTimeIcon color="primary" />
            <Typography variant="h6">
              Aktive Zeiterfassungen
            </Typography>
          </Box>
          <Chip 
            label={`${activeSessions.length} aktiv`}
            color={activeSessions.length > 0 ? "success" : "default"}
            size="small"
          />
        </Box>

        {activeSessions.length === 0 ? (
          <Box py={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Keine aktiven Zeiterfassungen
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {activeSessions.map((session, index) => (
              <React.Fragment key={session.id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    px: 0,
                    py: 2,
                    flexDirection: 'column',
                    alignItems: 'stretch',
                  }}
                >
                  {/* User and Status Row */}
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        {session.user_name}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {session.is_paused ? (
                        <Chip
                          icon={<PauseIcon />}
                          label="Pausiert"
                          size="small"
                          color="warning"
                          variant="outlined"
                          clickable
                          onClick={() => handleStopClick(session)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(237, 108, 2, 0.1)',
                            },
                          }}
                        />
                      ) : (
                        <Chip
                          icon={<PlayIcon />}
                          label="Läuft"
                          size="small"
                          color="success"
                          variant="outlined"
                          clickable
                          onClick={() => handleStopClick(session)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(46, 125, 50, 0.1)',
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Time and Location Row */}
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Gestartet: {formatStartTime(session.start_time)}
                      </Typography>
                      <Typography variant="h6" color="primary" fontWeight={600}>
                        {formatElapsedTime(session.elapsed_seconds)}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Box display="flex" alignItems="center" gap={0.5} justifyContent="flex-end">
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          Startort:
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                        {formatLocation(session.start_location)}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelStop}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Zeiterfassung beenden
        </DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie die Zeiterfassung von <strong>{confirmDialog.session?.user_name}</strong> wirklich beenden?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Die erfassten Zeiten werden automatisch als Zeiteintrag gespeichert und die Zeiterfassung beendet.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelStop}
            disabled={stopping}
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleConfirmStop}
            color="error"
            variant="contained"
            disabled={stopping}
            startIcon={stopping ? <CircularProgress size={16} /> : <StopIcon />}
          >
            {stopping ? 'Wird beendet...' : 'Beenden'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ActiveTimers;
